import axios from 'axios';
import { createAuthHeaders } from '../api-client';
import {
  WorkspaceKeyType,
  GenerateKeyDto,
  RotateKeyDto,
  RevokeKeyDto,
  KeyWithSecret,
  KeyListItem,
} from '@optidev/shared-types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const workspaceKeysApi = {
  /**
   * Generate a new API key
   * @param token - Firebase auth token
   * @param keyType - Type of key (defaults to CONTENT)
   */
  async generate(token: string, keyType?: WorkspaceKeyType): Promise<KeyWithSecret> {
    const response = await axios.post(
      `${API_URL}/api/workspace-keys/generate`,
      { keyType },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Check if workspace has an active API key
   * Available to all workspace members (not admin-only)
   */
  async checkExists(token: string): Promise<{ hasActiveKey: boolean }> {
    const response = await axios.get(`${API_URL}/api/workspace-keys/check-exists`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * List all workspace API keys
   */
  async list(token: string, includeRevoked: boolean = false): Promise<KeyListItem[]> {
    const params = new URLSearchParams();
    if (includeRevoked) {
      params.append('includeRevoked', 'true');
    }

    const response = await axios.get(`${API_URL}/api/workspace-keys?${params.toString()}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Rotate an API key secret
   */
  async rotate(token: string, keyId: string): Promise<KeyWithSecret> {
    const response = await axios.post(
      `${API_URL}/api/workspace-keys/rotate`,
      { keyId },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Revoke an API key
   */
  async revoke(token: string, keyId: string, reason?: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${API_URL}/api/workspace-keys/revoke`,
      { keyId, reason },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Get decrypted secret for a key
   */
  async getSecret(token: string, keyId: string): Promise<{ secret: string; version: number }> {
    const response = await axios.get(
      `${API_URL}/api/workspace-keys/${keyId}/secret`,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },
};
